import { MAIN } from '@/configs/main.config';
import { DYN_ACTIONS as dyn } from './actions';
import { ENV } from '@/configs/env.config';
import { EModeValue } from './enums/mode.enum';
import { EEntity } from './enums/entity.enum';
import { logger } from '../logger.service';
import { MockedDynamics } from './mock';
import { phoneCallApiMapper } from './mappers/phone-call.mapper';
import { IPhoneCall } from '@/shared/interfaces/phone-call.interface';
import { IDynEnvironmentApi } from './interfaces/api/dyn-environment.interface';
import { IDynamicsApi } from './interfaces/api/dynamics.interface';
import {
  ESolgariCallType,
  EStatusCode,
  IPhoneCallApi,
  IPhoneCallCreateEntityApi,
} from './interfaces/api/phone-call.interface';
import { searchEntityMapper } from './mappers/entity.mapper';
import {
  ICreateEntityResponse,
  ISearchAccountEntityApi,
  ISearchContactEntityApi,
  ISearchLeadEntityApi,
  ISearchSystemUserEntityApi,
  ISearchParams,
} from './interfaces/api/entity.interface';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { EParticipant } from './enums/participant.enum';
import { Timer } from '@/shared/helpers/timer.helper';
import { relateToEntityMapper } from './mappers/relate-to.mapper';
import { IRelateTo } from '@/shared/interfaces/relate-to.interface';
import { getPluralEntityName } from './helpers/get-plural-entity-name.helper';
import { FakeTimer } from '@/pages/CallRecord/CallRecord';
import { IChatNoteEntityApi } from './interfaces/api/chat-note.interface';
import { chatNoteApiMapper } from './mappers/chat-note.mapper';
import { ICrmChatNote } from '@/shared/interfaces/crm-chat-note.interface';
import {
  ICreateChatNoteParams,
  ICreateChatNoteResponse,
} from './interfaces/api/create-chat-note.interface';
import { countMilliseconds } from '@/shared/helpers/count-milliseconds';
import { IParticipantEntityApi } from './interfaces/api/participant-entity.interface';
import {
  IMarketingListApi,
  IMembersListApi,
} from './interfaces/api/marketing-list.interface';
import { ICrmMarketingList } from '@/shared/interfaces/crm-marketing-list.interface';
import {
  marketingListApiMapper,
  membersListApiMapper,
} from './mappers/marketing-list.mapper';
import { WebApi } from '../web-api/web-api.service';
import { getDomain } from '@/shared/helpers/get-domain.helper';
import { SolgariApiService } from '../solgari-api/solgari-api.service';
import { ICallNoteParams } from './interfaces/call-note.interface';
import { getCallStatus } from './helpers/get-call-status.helper';
import { ISession } from '../sip-js.service';
import { MemberType } from '@/shared/interfaces/crm-marketing-list.interface';
import { UnionEntityApiType } from '../dynamics/interfaces/api/entity.interface';
import { templateMap } from '../web-api/templates/entity.template';

declare global {
  const Microsoft: { CIFramework: IDynamicsApi };
}

const ACTIVE_RECORDS_FILTER = ' and statecode eq 0';

export class Dynamics {
  private static currentEnv: IDynEnvironmentApi = null;

  private static states = {
    res: null,
    rej: null,
  };

  private static preScriptPromise = new Promise<IDynamicsApi>(
    (resolve, reject) => {
      Dynamics.states.res = resolve;
      Dynamics.states.rej = reject;
    },
  );

  public static async getOwnerId(): Promise<string> {
    try {
      const dyn = await Dynamics.preScriptExec();
      const email = SolgariApiService.getOwnEmail();
      const query = `?$select=applicationid,domainname&$filter=domainname eq '${email}'`;
      const stringResult = await dyn.searchAndOpenRecords(
        EEntity.SYSTEM_USER,
        query,
        true,
      );
      const res = await JSON.parse(stringResult);
      return res[0].ownerid;
    } catch (err) {
      logger.error('[Dynamics] Unable to get owner id', err);
    }
  }

  public static preScriptExec(): Promise<IDynamicsApi> {
    return Dynamics.preScriptPromise;
  }

  public static createScript(): void {
    const widgetScript = document.querySelector(
      `script[data-cifid='${MAIN.cif_id}']`,
    );

    if (ENV.MOCK_DYN) {
      (window as any).Microsoft = { CIFramework: MockedDynamics };
      Dynamics.setEvents(true);
      return;
    }

    if (widgetScript) {
      return;
    }

    const script = document.createElement('script');

    const domain = getDomain();

    const https = 'https://';
    script.src = `${https}${domain}${MAIN.widget_script_path}`;
    script.dataset.crmurl = `${https}${domain}`;
    script.dataset.cifid = MAIN.cif_id;
    script.async = true;

    Dynamics.setEvents();
    document.body.appendChild(script);
  }

  public static async setMode(mode: EModeValue): Promise<string> {
    const Dyn = await Dynamics.preScriptExec();

    return Dyn.setMode(mode);
  }

  public static async getMode(): Promise<EModeValue> {
    const Dyn = await Dynamics.preScriptExec();

    return Dyn.getMode();
  }

  // TODO: recheck, afraid in some cases need to use async function,
  // because there are a lot of async function exp: async injectScript -> async getEnv -> getVersion
  // just keep it in mind, if see you something there that should be v2 but there in no Dynamics instance so create async function
  public static getVersion(): 1 | 2 {
    try {
      const env = Dynamics.getEnvSync();
      const version = JSON.parse(env.customParams)?.version;

      if (!version) {
        return 1;
      }

      return version;
    } catch (err) {
      logger.error('[Dynamics] Unable to get version', err);
      return 1;
    }
  }

  public static async getEnvironmentFromApi(): Promise<IDynEnvironmentApi> {
    const dyn = await Dynamics.preScriptExec();

    const data = await dyn.getEnvironment();

    this.currentEnv = JSON.parse(data);
    return this.currentEnv;
  }

  public static async getEnvAsync(): Promise<IDynEnvironmentApi> {
    await Dynamics.preScriptExec();

    return this.currentEnv;
  }

  public static getEnvSync(): IDynEnvironmentApi {
    return this.currentEnv;
  }

  public static async openChatNote(id: string): Promise<void> {
    try {
      const dyn = await Dynamics.preScriptExec();

      const selectString = '?$select=*';
      const filter = `&$filter=solgari_solgariconversationid eq ${id}${ACTIVE_RECORDS_FILTER}&$top=1`;

      const fullQuery = `${selectString}${filter}`;

      await dyn.searchAndOpenRecords(EEntity.CHAT_NOTE, fullQuery, false);
    } catch (error: any) {
      logger.error('[Dynamics] Error opening chat note', error);
    }
  }

  public static async openEntity(id: string, entity: EEntity): Promise<void> {
    const dyn = await Dynamics.preScriptExec();

    const selectString = '?$select=*';
    const filter = `&$filter=${entity}id eq ${id}`;

    const fullQuery = `${selectString}${filter}`;

    try {
      await dyn.searchAndOpenRecords(entity, fullQuery, false);
    } catch (error: any) {
      logger.error('[Dynamics] Error opening entity', error);
    }
  }

  public static async openPhoneCall(
    id: string,
  ): Promise<
    Pick<
      IPhoneCallApi,
      '@odata.etag' | 'activityadditionalparams' | 'activityid' | 'phonenumber'
    >
  > {
    const dyn = await Dynamics.preScriptExec();

    const selectString = '?$select=phonenumber,activityadditionalparams';
    const filter = `&$filter=activityid eq ${id}`;

    const fullQuery = `${selectString}${filter}`;

    try {
      const stringResult = await dyn.searchAndOpenRecords(
        EEntity.PHONE_CALL,
        fullQuery,
        false,
      );
      const result: IPhoneCallApi = JSON.parse(stringResult)[0];

      return result;
    } catch (error: any) {
      logger.error('[Dynamics] Unable to open phone call ', error);
    }
  }

  public static async getPhoneCalls(): Promise<IPhoneCall[]> {
    try {
      const sleep = (ms: number) => {
        return new Promise((resolve) => setTimeout(resolve, ms));
      };

      await sleep(1500);
      const operatorId = await Dynamics.getOwnerId();
      const dyn = await Dynamics.preScriptExec();

      const selectString = `?$select=actualdurationminutes,_regardingobjectid_value,_ownerid_value,subject,phonenumber,directioncode,senton,activityadditionalparams,createdon,solgari_durationformatted&$filter=_ownerid_value eq '${operatorId}'`;
      // solgari_durationformatted%20ne%20null
      const orderString = '&$orderby=createdon desc';
      const quantity = '&$top=100';

      const fullQuery = `${selectString}${orderString}${quantity}`;

      const stringResult = await dyn.searchAndOpenRecords(
        EEntity.PHONE_CALL,
        fullQuery,
        true,
      );
      const data: { [key: number]: IPhoneCallApi } = JSON.parse(stringResult);
      return Object.values(data).map(phoneCallApiMapper);
    } catch (error: unknown) {
      logger.error('[Dynamics] Unable to get phone calls ', error);

      return [];
    }
  }

  public static async saveNewEntity(
    type,
    data,
  ): Promise<ICreateEntityResponse> {
    const dyn = await Dynamics.preScriptExec();
    try {
      let result = null;

      result = await dyn.createRecord(type, JSON.stringify(data));

      return JSON.parse(result);
    } catch (error) {
      logger.error('[Dynamics] Unable to create new entity ', error);
    }
  }

  public static async updateRecord(
    type,
    id,
    data,
  ): Promise<ICreateEntityResponse> {
    const dyn = await Dynamics.preScriptExec();
    try {
      let result = null;

      result = await dyn.updateRecord(type, id, JSON.stringify(data));

      return JSON.parse(result);
    } catch (error) {
      logger.error('[Dynamics] Unable to update record ', error);
    }
  }

  public static async createChatNote(
    createNoteParams: ICreateChatNoteParams,
  ): Promise<ICreateChatNoteResponse> {
    try {
      const dyn = await Dynamics.preScriptExec();
      const ownerId = await Dynamics.getOwnerId();
      const jsonData = JSON.stringify({
        ...createNoteParams,
        [`ownerid_${EEntity.CHAT_NOTE}@odata.bind`]: `/systemusers(${ownerId})`,
      });
      const strResult = await dyn.createRecord(EEntity.CHAT_NOTE, jsonData);
      const result: ICreateChatNoteResponse = JSON.parse(strResult);

      return result;
    } catch (err) {
      logger.error('[Dynamics] Unable to create chat note ', err);
    }
  }

  public static async updateChatNote(
    id: string,
    createNoteParams: ICreateChatNoteParams,
  ): Promise<ICreateChatNoteResponse> {
    try {
      const dyn = await Dynamics.preScriptExec();
      const ownerId = await Dynamics.getOwnerId();
      const jsonData = JSON.stringify({
        ...createNoteParams,
        [`ownerid_${EEntity.CHAT_NOTE}@odata.bind`]: `/systemusers(${ownerId})`,
      });
      const strResult = await dyn.updateRecord(EEntity.CHAT_NOTE, id, jsonData);
      const result: ICreateChatNoteResponse = JSON.parse(strResult);

      return result;
    } catch (err) {
      logger.error('[Dynamics] Unable to update chat note ', err);
    }
  }

  public static async saveCallNoteAccurateTime(
    noteId: string,
    timer: Timer | FakeTimer,
    xSessionId: string,
    callState: EStatusCode,
    callType: ESolgariCallType,
  ): Promise<ICreateEntityResponse> {
    // return {} as any;

    const dyn = await Dynamics.preScriptExec();

    const minTime =
      timer instanceof Timer
        ? (timer as any)?.roundMinutes
        : (timer as any)?.minutes;

    const data: Partial<IPhoneCallCreateEntityApi> = {
      actualdurationminutes: minTime,
      solgari_durationmilliseconds: countMilliseconds(timer.time),
      solgari_durationformatted: timer.time,
      activityadditionalparams: xSessionId,
    };

    // Enrich data if there is a call State
    if (callState) {
      const callInfo = getCallStatus(callState);

      data.statuscode = callInfo.statuscode;
      data.statecode = callInfo.statecode;
    }

    // Enrich data if there is a call State
    if (callType) {
      data.solgari_calltype = callType;
    }

    // TODO: remove it later
    const tryMoreTimeIfErrorCb = async () => {
      let _result = null;

      delete data.statecode;
      delete data.statuscode;
      delete data.solgari_calltype;

      const jsonData = JSON.stringify(data);

      try {
        if (!noteId) {
          _result = await dyn.createRecord(EEntity.PHONE_CALL, jsonData);
        } else {
          _result = await dyn.updateRecord(
            EEntity.PHONE_CALL,
            noteId,
            jsonData,
          );
        }
      } catch (error) {
        logger.error('[Dynamics] Unable to update or crete record ', error);
      }

      return _result;
    };

    try {
      const jsonData = JSON.stringify(data);
      const result = await dyn.updateRecord(
        EEntity.PHONE_CALL,
        noteId,
        jsonData,
      );
      return JSON.parse(result);
    } catch (error) {
      logger.error('[Dynamics] Unable to update record ', error);

      return tryMoreTimeIfErrorCb();
    }
  }

  public static async savePhoneCallNote(
    params: ICallNoteParams,
    session?: ISession,
  ): Promise<ICreateEntityResponse> {
    const getId = (value: string) => {
      return value.replace(/{|}/g, '');
    };

    const dynData: IPhoneCallCreateEntityApi = {};

    const relatedInfo = params.relatedInfo;
    const entity = params.entity;
    const desc = params.desc;
    const callState = params.callState;
    const callType = params.callType;
    const noteId = params.noteId;
    const timer = params.timer;

    // Enrich data if there is a call (call session)
    if (session) {
      const xSessionId = session.xSessionId;
      let minutes: number;

      if (timer instanceof Timer) {
        minutes = (timer as any)?.roundMinutes;
      } else minutes = (timer as any)?.minutes;

      // populate dynData
      dynData.activityadditionalparams = xSessionId;
      dynData.directioncode = session.direction === 'outgoing';
      dynData.actualdurationminutes = minutes;
      dynData.solgari_durationformatted = timer.time;
      dynData.solgari_durationmilliseconds = countMilliseconds(timer.time);
    }
    const currentEnv = Dynamics.getEnvSync();
    const sysUserId = getId(currentEnv.userId);
    const us: IParticipantEntityApi = {
      'partyid_systemuser@odata.bind': `/systemusers(${sysUserId})`,
      participationtypemask:
        session?.direction === 'incoming' ? EParticipant.TO : EParticipant.FROM,
    };

    if (entity) {
      const phoneNumber = entity.initialPhone;
      const extendedSubject = params.extendedSubject;

      const subject = !extendedSubject
        ? `Call with ${entity.fullName} (${phoneNumber})`
        : `Call with ${entity.fullName} (${phoneNumber})${extendedSubject}`;

      const entityEtn = entity.type;
      const entityId = entity.id;

      // populate dynData
      dynData.subject = subject;
      dynData.phonenumber = phoneNumber;

      if (entityEtn) {
        // populate dynData
        dynData[`regardingobjectid_${entityEtn}@odata.bind`] =
          `/${getPluralEntityName(entityEtn)}(${entityId})`;
      }
      // If we have internal calls send only us, otherwise - both
      if (sysUserId && session) {
        if (!entityId) {
          dynData.phonecall_activity_parties = [us];
        } else {
          const them: IParticipantEntityApi = {
            participationtypemask:
              session?.direction === 'incoming'
                ? EParticipant.FROM
                : (EParticipant.TO ?? EParticipant.FROM),
          };
          them[`partyid_${entityEtn}@odata.bind`] =
            `/${entityEtn}s(${entityId})`;

          dynData.phonecall_activity_parties = [us, them];
        }
      }
    }

    if (relatedInfo?.type && relatedInfo?.id) {
      dynData[`regardingobjectid_${relatedInfo.type}@odata.bind`] =
        `/${getPluralEntityName(relatedInfo.type)}(${relatedInfo.id})`;
    }

    // Enrich data if there is a description for note
    if (desc) {
      dynData.description = desc;
    }

    // Enrich data if there is a call State
    if (callState) {
      const callInfo = getCallStatus(callState);

      dynData.statuscode = callInfo.statuscode;
      dynData.statecode = callInfo.statecode;
    }

    // Enrich data if there is a call type
    if (callType) {
      dynData.solgari_calltype = callType;
    }

    const jsonData = JSON.stringify(dynData);

    const dyn = await Dynamics.preScriptExec();

    let result = null;

    // TODO: remove it later
    const tryMoreTimeIfErrorCb = async () => {
      let _result = null;

      delete dynData.statecode;
      delete dynData.statuscode;
      delete dynData.solgari_calltype;

      const jsonData = JSON.stringify(dynData);

      try {
        if (!noteId) {
          _result = await dyn.createRecord(EEntity.PHONE_CALL, jsonData);
        } else {
          _result = await dyn.updateRecord(
            EEntity.PHONE_CALL,
            noteId,
            jsonData,
          );
        }
      } catch (error) {
        logger.error('[Dynamics] Unable to update record ', error);
      }

      return _result;
    };

    try {
      if (!noteId) {
        result = await dyn.createRecord(EEntity.PHONE_CALL, jsonData);
      } else {
        result = await dyn.updateRecord(EEntity.PHONE_CALL, noteId, jsonData);
      }

      return JSON.parse(result);
    } catch (error) {
      logger.error('[Dynamics] Unable to create or update record ', error);
      return tryMoreTimeIfErrorCb();
    }
  }

  public static async searchEntities(
    search?: string,
    isExactPhoneSearch?: boolean,
    isIgnoreLengthCheck?: boolean,
  ): Promise<IEntity[]> {
    try {
      let cleanedPhone = '';
      const isPhone = /^(\+|^[0-9])\d+$/.test(search);
      if (isPhone) {
        const phoneDigitsQuantity = -8;
        cleanedPhone = search
          .replace('+', '')
          .trim()
          .slice(phoneDigitsQuantity);
        if (!isIgnoreLengthCheck && cleanedPhone.length < 5) {
          return;
        }
      }

      const [users, accounts, contacts, leads] = await Promise.all([
        Dynamics.searchUsers({
          phone: cleanedPhone,
          text: search,
        }),
        Dynamics.searchAccounts({
          phone: cleanedPhone,
          text: search,
          isExactPhoneSearch: isExactPhoneSearch,
        }),
        Dynamics.searchContacts({
          phone: cleanedPhone,
          text: search,
          isExactPhoneSearch: isExactPhoneSearch,
        }),
        Dynamics.searchLeads({
          phone: cleanedPhone,
          text: search,
          isExactPhoneSearch: isExactPhoneSearch,
        }),
      ]);

      return [...users, ...accounts, ...contacts, ...leads];
    } catch (error) {
      logger.error(`[Dynamics] Error while searching entities : ${error}`);

      return [];
    }
  }

  public static async searchEntityByCalls({
    phone,
    id,
    isExactPhoneSearch,
  }: {
    phone?: string;
    id?: string;
    isExactPhoneSearch?: boolean;
  }): Promise<IEntity[]> {
    try {
      let cleanedPhone = '';

      if (phone) {
        const isNum = /^(\+|^[0-9])\d+$/.test(phone);
        const phoneDigitsQuantity = -8;
        cleanedPhone = isNum
          ? phone.replace('+', '').trim().slice(phoneDigitsQuantity)
          : phone.trim().slice(phoneDigitsQuantity);
        if (cleanedPhone.length < 5) {
          return;
        }
      }

      const users = await Dynamics.searchUsers({
        phone: cleanedPhone,
        text: phone,
        id,
      });
      const accounts = await Dynamics.searchAccounts({
        phone: cleanedPhone,
        text: phone,
        id,
        isExactPhoneSearch,
      });
      const contacts = await Dynamics.searchContacts({
        phone: cleanedPhone,
        text: phone,
        id,
        isExactPhoneSearch,
      });
      const leads = await Dynamics.searchLeads({
        phone: cleanedPhone,
        text: phone,
        id,
        isExactPhoneSearch,
      });

      const allEntities = [...users, ...accounts, ...contacts, ...leads];

      return allEntities;
    } catch (error) {
      logger.error(`[Dynamics] Search entity error : ${error}`);

      return [];
    }
  }

  public static async searchContacts({
    id,
    phone,
    text,
    isExactPhoneSearch,
  }: ISearchParams): Promise<IEntity[]> {
    try {
      const dyn = await Dynamics.preScriptExec();
      const selectString =
        '?$select=contactid,fullname,lastname,mobilephone,telephone1,telephone2,telephone3,emailaddress1,emailaddress2,emailaddress3';
      const orderString = '&$orderby=fullname';
      const quantity = '&$top=100';

      let filter = '';

      if (id) {
        filter += `&$filter=contactid eq ${id}`;
      } else {
        if (phone) {
          const envInfo: any = await Microsoft.CIFramework.getEnvironment();
          let additionalFields = '';
          if (isExactPhoneSearch) {
            filter += `&$filter=(endswith(telephone1,'${phone}') or endswith(telephone2,'${phone}') or endswith(telephone3,'${phone}') or endswith(mobilephone,'${phone}')${additionalFields})`;
          } else {
            filter += `&$filter=(contains(telephone1,'${phone}') or contains(telephone2,'${phone}') or contains(telephone3,'${phone}') or contains(mobilephone,'${phone}')${additionalFields})`;
          }
          if (envInfo.includes('bfobdev')) {
            additionalFields = ` or contains(new_maphone,'${phone}') `;
          }
        } else {
          filter += filter
            ? ` and (contains(fullname,'${text}') or contains(emailaddress1,'${text}') or contains(emailaddress2,'${text}') or contains(emailaddress3,'${text}'))`
            : `&$filter=(contains(fullname,'${text}') or contains(emailaddress1,'${text}') or contains(emailaddress2,'${text}') or contains(emailaddress3,'${text}'))`;
        }
      }

      const fullQuery = `${selectString}${orderString}${quantity}${filter}${ACTIVE_RECORDS_FILTER}`;

      const contactsStr = await dyn.searchAndOpenRecords(
        EEntity.CONTACT,
        fullQuery,
        true,
      );
      const contacts: ISearchContactEntityApi = JSON.parse(contactsStr);
      return Object.values(contacts).map((entity) =>
        searchEntityMapper<ISearchContactEntityApi>(
          entity,
          EEntity.CONTACT,
          text,
        ),
      );
    } catch (error: any) {
      logger.error('[Dynamics] Error while searching contacts ', error);

      return [];
    }
  }

  public static async searchAccounts({
    id,
    phone,
    text,
    isExactPhoneSearch,
  }: ISearchParams): Promise<IEntity[]> {
    try {
      const dyn = await Dynamics.preScriptExec();
      const selectString =
        '?$select=accountid,name,telephone1,telephone2,telephone3,emailaddress1,emailaddress2,emailaddress3';
      const orderString = '&$orderby=name';
      const quantity = '&$top=100';
      let filter = '';

      if (id) {
        filter += `&$filter=accountid eq ${id}`;
      } else {
        if (phone) {
          if (isExactPhoneSearch) {
            filter += `&$filter=(endswith(telephone1,'${phone}') or endswith(telephone2,'${phone}') or endswith(telephone3,'${phone}'))`;
          } else {
            filter += `&$filter=(contains(telephone1,'${phone}') or contains(telephone2,'${phone}') or contains(telephone3,'${phone}'))`;
          }
        } else {
          filter += filter
            ? ` and (contains(name,'${text}') or contains(emailaddress1,'${text}') or contains(emailaddress2,'${text}') or contains(emailaddress3,'${text}'))`
            : `&$filter=(contains(name,'${text}') or contains(emailaddress1,'${text}') or contains(emailaddress2,'${text}') or contains(emailaddress3,'${text}'))`;
        }
      }

      const fullQuery = `${selectString}${orderString}${quantity}${filter}${ACTIVE_RECORDS_FILTER}`;

      const accountsStr = await dyn.searchAndOpenRecords(
        EEntity.ACCOUNT,
        fullQuery,
        true,
      );
      const accounts: ISearchAccountEntityApi = JSON.parse(accountsStr);
      return Object.values(accounts).map((entity) =>
        searchEntityMapper<ISearchAccountEntityApi>(
          entity,
          EEntity.ACCOUNT,
          text,
        ),
      );
    } catch (error: unknown) {
      logger.error('[Dynamics] Error while searching accounts ', error);

      return [];
    }
  }

  public static async searchLeads({
    id,
    phone,
    text,
    isExactPhoneSearch,
  }: ISearchParams): Promise<IEntity[]> {
    try {
      const dyn = await Dynamics.preScriptExec();
      const selectString =
        '?$select=leadid,fullname,telephone1,telephone2,telephone3,mobilephone,emailaddress1,emailaddress2,emailaddress3';
      const orderString = '&$orderby=fullname';
      const quantity = '&$top=100';
      let filter = '';

      if (id) {
        filter += `&$filter=leadid eq ${id}`;
      } else {
        if (phone) {
          if (isExactPhoneSearch) {
            filter += `&$filter=(endswith(telephone1,'${phone}') or endswith(telephone2,'${phone}') or endswith(telephone3,'${phone}') or endswith(mobilephone,'${phone}'))`;
          } else {
            filter += `&$filter=(contains(telephone1,'${phone}') or contains(telephone2,'${phone}') or contains(telephone3,'${phone}') or contains(mobilephone,'${phone}'))`;
          }
        } else {
          filter += filter
            ? ` and (contains(fullname,'${text}') or contains(emailaddress1,'${text}') or contains(emailaddress2,'${text}') or contains(emailaddress3,'${text}'))`
            : `&$filter=(contains(fullname,'${text}') or contains(emailaddress1,'${text}') or contains(emailaddress2,'${text}') or contains(emailaddress3,'${text}'))`;
        }
      }

      const fullQuery = `${selectString}${orderString}${quantity}${filter}${ACTIVE_RECORDS_FILTER}`;

      const leadsStr = await dyn.searchAndOpenRecords(
        EEntity.LEAD,
        fullQuery,
        true,
      );
      const leads: ISearchLeadEntityApi = JSON.parse(leadsStr);
      return Object.values(leads).map((entity) =>
        searchEntityMapper<ISearchLeadEntityApi>(entity, EEntity.LEAD, text),
      );
    } catch (error: unknown) {
      logger.error('[Dynamics] Error while searching leads ', error);

      return [];
    }
  }

  public static async searchUsers({
    id,
    phone,
    text,
  }: ISearchParams): Promise<IEntity[]> {
    try {
      const dyn = await Dynamics.preScriptExec();
      const selectString =
        '?$select=systemuserid,fullname,address1_telephone1,mobilephone,internalemailaddress';
      const orderString = '&$orderby=fullname';
      const quantity = '&$top=10';
      let filter = '';

      if (id) {
        filter += `&$filter=systemuserid eq ${id}`;
      } else {
        if (phone) {
          filter += filter
            ? ` and address1_telephone1 eq '${phone}' or mobilephone eq '${phone}'`
            : `&$filter=address1_telephone1 eq '${phone}' or mobilephone eq '${phone}'`;
        } else {
          filter += filter
            ? ` and internalemailaddress eq '${phone}'`
            : `&$filter=internalemailaddress eq '${phone}'`;
        }
      }

      const fullQuery = `${selectString}${orderString}${quantity}${filter}`;

      const usersStr = await dyn.searchAndOpenRecords(
        EEntity.SYSTEM_USER,
        fullQuery,
        true,
      );
      const users: ISearchSystemUserEntityApi = JSON.parse(usersStr);
      return Object.values(users).map((entity) =>
        searchEntityMapper<ISearchSystemUserEntityApi>(
          entity,
          EEntity.SYSTEM_USER,
          text,
        ),
      );
    } catch (error: unknown) {
      logger.error('[Dynamics] Error while searching users ', error);

      return [];
    }
  }

  /**
   *
   * @param id - chat id
   * @returns
   */
  public static async searchChatNotes({
    id,
  }: ISearchParams): Promise<ICrmChatNote[]> {
    try {
      const dyn = await Dynamics.preScriptExec();
      const selectString = '?$select=*';
      let filter = '';

      if (id) {
        filter += `&$filter=solgari_solgariconversationid eq ${id}`;
      }

      const fullQuery = `${selectString}${filter}${filter ? ACTIVE_RECORDS_FILTER : '&$filter=statecode eq 0'}`;
      const chatNotesStr = await dyn.searchAndOpenRecords(
        EEntity.CHAT_NOTE,
        fullQuery,
        true,
      );
      const chatNotes: IChatNoteEntityApi[] = JSON.parse(chatNotesStr);

      return Object.values(chatNotes).map((entity) =>
        chatNoteApiMapper(entity),
      );
    } catch (error: unknown) {
      logger.error('[Dynamics] Error while searching chat notes ', error);

      return [];
    }
  }

  public static async searchRelateToEntities(
    entityType: EEntity,
    searchText?: string,
  ) {
    try {
      const dyn = await Dynamics.preScriptExec();

      const getNameField = (type: EEntity): string => {
        switch (type) {
          case EEntity.ACCOUNT:
            return 'name';
          case EEntity.CONTACT:
            return 'fullname';
          case EEntity.LEAD:
            return 'fullname';
          case EEntity.OPPORTUNITY:
            return 'name';
          case EEntity.CASE:
            return 'title';
          case EEntity.CAMPAIGN:
            return 'name';
          case EEntity.QUOTE:
            return 'name';
          case EEntity.INVOICE:
            return 'name';
          case EEntity.ORDER:
            return 'name';
          default:
            return 'name';
        }
      };

      const nameField = getNameField(entityType);

      let query = `?$select=*&$top=20`;
      if (searchText && searchText.trim().length > 0) {
        const encoded = searchText.replace(/'/g, "''");
        query += `&$filter=contains(${nameField},'${encoded}')${ACTIVE_RECORDS_FILTER}`;
      } else {
        query += `&$filter=statecode eq 0`;
      }
      query += `&$orderby=${nameField}`;

      const data = await dyn.searchAndOpenRecords(entityType, query, true);
      const allData = JSON.parse(data);
      const finalData = Object.values(allData).map((entity) =>
        relateToEntityMapper(entity, entityType),
      );
      return finalData;
    } catch (error: unknown) {
      logger.error(
        '[Dynamics] Error while searching related to entities ',
        error,
      );

      return [];
    }
  }

  public static async retrieveCallRecord({
    entityLogicalName,
    id,
    options,
  }: {
    entityLogicalName: EEntity;
    id: string;
    options: string;
  }): Promise<any> {
    const dyn = await Dynamics.preScriptExec();

    try {
      const recordStr = await dyn.retrieveRecord(
        entityLogicalName,
        id,
        options,
      );
      const record: any = JSON.parse(recordStr);

      return record;
    } catch (error: any) {
      logger.error('[Dynamics] Error while retrieving call record ', error);
    }
  }

  public static async getRelateToOption(
    entityLogicalName: EEntity,
    id: string,
  ): Promise<IRelateTo | null> {
    const dyn = await Dynamics.preScriptExec();

    try {
      const options = '?$select=*';
      const recordStr = await dyn.retrieveRecord(
        entityLogicalName,
        id,
        options,
      );
      const record: any = JSON.parse(recordStr);

      return relateToEntityMapper(record, entityLogicalName);
    } catch (error: any) {
      logger.error('[Dynamics] Error while getting relate to option ', error);
      return null;
    }
  }

  public static async getMarketingList(
    filter?: string,
  ): Promise<ICrmMarketingList[]> {
    try {
      const ownerId = await Dynamics.getOwnerId();
      const dyn = await Dynamics.preScriptExec();
      let query = `?$select=*&$filter=_ownerid_value eq ${ownerId}${ACTIVE_RECORDS_FILTER}`;
      if (filter) {
        query += ` and ${filter}`;
      }
      query += '&$top=100';
      const listString = await dyn.searchAndOpenRecords(
        EEntity.MARKETING_LIST,
        query,
        true,
      );
      const list: IMarketingListApi[] = JSON.parse(listString);

      return Object.values(list).map(marketingListApiMapper);
    } catch (error: unknown) {
      logger.error('[Dynamics] Error while getting marketing list ', error);
    }
  }

  public static async getContactsForMarketingList(
    marketingList: ICrmMarketingList,
  ): Promise<IEntity[]> {
    try {
      const { listId, type, query, memberType } = marketingList;

      const dyn = await Dynamics.preScriptExec();

      if (type === 'Dynamic') {
        const data = await WebApi.getEntitiesForDynamicList(memberType, query);
        const result = data.map((v) =>
          searchEntityMapper(v, memberType as EEntity, null),
        );

        return result;
      }

      if (type === 'Static') {
        const fullQuery = `?$select=*&$filter=_listid_value eq ${listId}`;

        const listString = await dyn.searchAndOpenRecords(
          EEntity.LIST_MEMBER,
          fullQuery,
          true,
        );
        const list: IMembersListApi[] = JSON.parse(listString);
        const memberIds = Object.values(list).map(membersListApiMapper);
        const data = await this.getEntitiesForStaticList(memberType, memberIds);
        const result = Object.values(data).map((v) =>
          searchEntityMapper(v, memberType as EEntity, null),
        );

        return result;
      }

      return [];
    } catch (error: unknown) {
      logger.error(
        '[Dynamics] Unable to get contacts for marketing list ',
        error,
      );

      return [];
    }
  }

  public static async searchAndOpenRecordWrapper(
    entityName: EEntity,
    query: string,
    searchOnly: boolean,
    searchType?: string,
  ) {
    const dyn = await Dynamics.preScriptExec();
    await dyn.searchAndOpenRecords(entityName, query, searchOnly, searchType);
  }

  public static async getEntitiesForStaticList(
    type: MemberType,
    memberIds: string[],
  ): Promise<UnionEntityApiType[]> {
    try {
      const dyn = await Dynamics.preScriptExec();
      const filterQuery = memberIds
        .map((i) => `${type}id eq ${i}`)
        .join(' or ');
      const select = `$select=${templateMap[type].join(',')}`;
      const query = `${select}&$filter=${filterQuery}`;
      const stringResult = await dyn.searchAndOpenRecords(
        type as EEntity,
        query,
        true,
      );
      const result = JSON.parse(stringResult);
      return result;
    } catch (err) {
      logger.error(
        '[Dynamics] Error while getting entities for static list ',
        err,
      );
    }
  }

  private static setEvents(shouldSkip?: boolean): void {
    if (shouldSkip) {
      Dynamics.states.res(Microsoft.CIFramework);
      return;
    }

    window.addEventListener('CIFInitDone', () => {
      Microsoft.CIFramework.addHandler(
        dyn.clickToAct.event,
        dyn.clickToAct.action,
      );
      Microsoft.CIFramework.addHandler(
        dyn.modeChanged.event,
        dyn.modeChanged.action,
      );
      Microsoft.CIFramework.addHandler(
        dyn.pageNavigation.event,
        dyn.pageNavigation.action,
      );
      // v2
      Microsoft.CIFramework.addHandler(
        dyn.onSessionSwitched.event,
        dyn.onSessionSwitched.action,
      );
      Microsoft.CIFramework.addHandler(
        dyn.onSessionClosed.event,
        dyn.onSessionClosed.action,
      );

      Dynamics.states.res(Microsoft.CIFramework);
    });
  }

  private static generateBrowserTabId(): string {
    const s4 = () => {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    };
    return (
      s4() +
      s4() +
      '-' +
      s4() +
      '-' +
      s4() +
      '-' +
      s4() +
      '-' +
      s4() +
      s4() +
      s4()
    );
  }
}
